import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, MapPin, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { classifyErr } from "@/lib/admin";
import { useAuth } from "@/lib/auth";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import ChangeHistoryPanel from "@/components/bulk/ChangeHistoryPanel";
import PageHeader from "@/components/PageHeader";

export default function Geography() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("masters.manage");
  return (
    <div className="space-y-5" data-testid="admin-geography">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Geography" }]}
        title="Geography"
        subtitle="Zones → States → Sub Zones → Cities for zone + territory routing."
        favorite={{ key: "admin.geography", type: "screen", label: "Geography", route: "/admin/geography" }}
        testid="geography-page-header"
      />


      <Tabs defaultValue="zones" className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList className="bg-neutral-100 rounded-xl h-11 p-1">
            <TabsTrigger value="zones" data-testid="geo-tab-zones">Zones</TabsTrigger>
            <TabsTrigger value="states" data-testid="geo-tab-states">States</TabsTrigger>
            <TabsTrigger value="sub_zones" data-testid="geo-tab-sub-zones">Sub Zones</TabsTrigger>
            <TabsTrigger value="cities" data-testid="geo-tab-cities">Cities</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="zones">
          <GeoTabHeader masterKey="zones" label="Zones" entityType="zones" canManage={canManage} />
          <ZonesTab canManage={canManage} />
        </TabsContent>
        <TabsContent value="states">
          <GeoTabHeader masterKey="states" label="States" entityType="geography_states" canManage={canManage} />
          <StatesTab canManage={canManage} />
        </TabsContent>
        <TabsContent value="sub_zones">
          <GeoTabHeader masterKey="sub_zones" label="Sub Zones" entityType="sub_zones" canManage={canManage} />
          <SubZonesTab canManage={canManage} />
        </TabsContent>
        <TabsContent value="cities">
          <GeoTabHeader masterKey="cities" label="Cities" entityType="geography_cities" canManage={canManage} />
          <CitiesTab canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GeoTabHeader({ masterKey, label, entityType, canManage }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return (
    <>
      <div className="flex items-center justify-end gap-2 mb-3">
        <Button
          variant="outline"
          onClick={() => setHistoryOpen(true)}
          className="h-10"
          data-testid={`geo-history-${masterKey}`}
        >
          <History className="h-4 w-4 mr-2" /> History
        </Button>
        <BulkActionsBar
          masterKey={masterKey}
          label={label}
          canImport={canManage}
          canExport
        />
      </div>
      <ChangeHistoryPanel
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType={entityType}
        title={`${label} — change history`}
      />
    </>
  );
}

function ZonesTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({ code: "", name: "" });
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/zones", { params: { include_inactive: true } });
      setItems(data.items);
    } catch (e) { toast.error(classifyErr(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/zones", newItem); setNewItem({ code: "", name: "" }); toast.success("Zone created"); load(); }
    catch (e) { toast.error(classifyErr(e)); }
  };
  const save = async (row, patch) => { try { await api.patch(`/zones/${row.id}`, patch); toast.success("Saved"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const del = async (row) => { try { await api.delete(`/zones/${row.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-4">
        <SimpleTable
          columns={["Code", "Name", "Active", ""]}
          rows={items}
          busy={busy}
          canManage={canManage}
          testid="geo-zones-table"
          renderRow={(row) => (
            <EditableGeoRow
              key={row.id}
              row={row}
              fields={[{ key: "code", readonly: true }, { key: "name" }]}
              canManage={canManage}
              onSave={save}
              onDelete={del}
            />
          )}
        />
        {canManage && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <div>
              <Label className="text-xs">Code</Label>
              <Input value={newItem.code} onChange={(e) => setNewItem({ ...newItem, code: e.target.value.toUpperCase() })} placeholder="MH" className="h-9 mt-1" data-testid="geo-zone-new-code" />
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="Maharashtra" className="h-9 mt-1" data-testid="geo-zone-new-name" />
            </div>
            <Button onClick={create} disabled={!newItem.code || !newItem.name} className="tulsi-gradient text-white h-9" data-testid="geo-zone-add">
              <Plus className="h-3 w-3 mr-1" /> Add zone
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatesTab({ canManage }) {
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({ name: "" });
  useEffect(() => { api.get("/zones").then((r) => setZones(r.data.items)).catch(() => {}); }, []);
  const load = useCallback(async () => {
    if (!zoneId) { setItems([]); return; }
    setBusy(true);
    try { const { data } = await api.get("/geography/states", { params: { zone_id: zoneId, include_inactive: true } }); setItems(data.items); }
    catch (e) { toast.error(classifyErr(e)); } finally { setBusy(false); }
  }, [zoneId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post("/geography/states", { name: newItem.name, zone_id: zoneId }); setNewItem({ name: "" }); toast.success("State created"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const save = async (row, patch) => { try { await api.patch(`/geography/states/${row.id}`, patch); toast.success("Saved"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const del = async (row) => { try { await api.delete(`/geography/states/${row.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Label className="text-xs">Zone</Label>
          <Select value={zoneId || "none"} onValueChange={(v) => setZoneId(v === "none" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="geo-states-zone-filter"><SelectValue placeholder="Pick a zone" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Select zone —</SelectItem>
              {zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {zoneId ? (
          <>
            <SimpleTable
              columns={["Name", "Active", ""]}
              rows={items}
              busy={busy}
              canManage={canManage}
              testid="geo-states-table"
              renderRow={(row) => (
                <EditableGeoRow key={row.id} row={row} fields={[{ key: "name" }]} canManage={canManage} onSave={save} onDelete={del} />
              )}
            />
            {canManage && (
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">New state</Label>
                  <Input value={newItem.name} onChange={(e) => setNewItem({ name: e.target.value })} className="h-9 mt-1" data-testid="geo-state-new-name" />
                </div>
                <Button onClick={create} disabled={!newItem.name} className="tulsi-gradient text-white h-9" data-testid="geo-state-add">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-neutral-500 py-6 text-center">Select a zone to view its states.</div>
        )}
      </CardContent>
    </Card>
  );
}

function SubZonesTab({ canManage }) {
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [stateId, setStateId] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({ code: "", name: "" });
  useEffect(() => { api.get("/zones").then((r) => setZones(r.data.items)).catch(() => {}); }, []);
  useEffect(() => {
    if (!zoneId) { setStates([]); setStateId(""); return; }
    api.get("/geography/states", { params: { zone_id: zoneId } })
       .then((r) => setStates(r.data.items || []))
       .catch(() => setStates([]));
    setStateId("");
  }, [zoneId]);
  const load = useCallback(async () => {
    if (!stateId) { setItems([]); return; }
    setBusy(true);
    try { const { data } = await api.get("/geography/sub-zones", { params: { state_id: stateId, include_inactive: true } }); setItems(data.items || []); }
    catch (e) { toast.error(classifyErr(e)); } finally { setBusy(false); }
  }, [stateId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post("/geography/sub-zones", { code: newItem.code, name: newItem.name, state_id: stateId }); setNewItem({ code: "", name: "" }); toast.success("Sub Zone created"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const save = async (row, patch) => { try { await api.patch(`/geography/sub-zones/${row.id}`, patch); toast.success("Saved"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const del = async (row) => { try { await api.delete(`/geography/sub-zones/${row.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="text-xs">Zone</Label>
          <Select value={zoneId || "none"} onValueChange={(v) => setZoneId(v === "none" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="geo-sub-zones-zone-filter"><SelectValue placeholder="Pick a zone" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Select zone —</SelectItem>
              {zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Label className="text-xs">State</Label>
          <Select value={stateId || "none"} onValueChange={(v) => setStateId(v === "none" ? "" : v)} disabled={!zoneId}>
            <SelectTrigger className="h-9 w-56" data-testid="geo-sub-zones-state-filter"><SelectValue placeholder={zoneId ? "Pick a state" : "Pick zone first"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Select state —</SelectItem>
              {states.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {stateId ? (
          <>
            <SimpleTable
              columns={["Code", "Name", "Active", ""]}
              rows={items} busy={busy} canManage={canManage}
              testid="geo-sub-zones-table"
              renderRow={(row) => (
                <EditableGeoRow key={row.id} row={row}
                  fields={[{ key: "code" }, { key: "name" }]}
                  canManage={canManage} onSave={save} onDelete={del} />
              )}
            />
            {canManage && (
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">New sub-zone code</Label>
                  <Input value={newItem.code} onChange={(e) => setNewItem((s) => ({ ...s, code: e.target.value.toUpperCase() }))} className="h-9 mt-1 w-40" data-testid="geo-sub-zone-new-code" />
                </div>
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={newItem.name} onChange={(e) => setNewItem((s) => ({ ...s, name: e.target.value }))} className="h-9 mt-1 w-64" data-testid="geo-sub-zone-new-name" />
                </div>
                <Button onClick={create} disabled={!newItem.code || !newItem.name} className="tulsi-gradient text-white h-9" data-testid="geo-sub-zone-add">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-neutral-500 py-6 text-center">Select a zone and state to view sub-zones.</div>
        )}
      </CardContent>
    </Card>
  );
}


function CitiesTab({ canManage }) {
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [stateId, setStateId] = useState("");
  const [subZones, setSubZones] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", sub_zone_id: "" });
  useEffect(() => { api.get("/zones").then((r) => setZones(r.data.items)).catch(() => {}); }, []);
  useEffect(() => {
    if (!zoneId) { setStates([]); setStateId(""); return; }
    api.get("/geography/states", { params: { zone_id: zoneId } }).then((r) => setStates(r.data.items)).catch(() => {});
  }, [zoneId]);
  useEffect(() => {
    if (!stateId) { setSubZones([]); return; }
    api.get("/geography/sub-zones", { params: { state_id: stateId } }).then((r) => setSubZones(r.data.items)).catch(() => setSubZones([]));
  }, [stateId]);
  const load = useCallback(async () => {
    if (!stateId) { setItems([]); return; }
    setBusy(true);
    try { const { data } = await api.get("/geography/cities", { params: { state_id: stateId, include_inactive: true } }); setItems(data.items); }
    catch (e) { toast.error(classifyErr(e)); } finally { setBusy(false); }
  }, [stateId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post("/geography/cities", { name: newItem.name, state_id: stateId, sub_zone_id: newItem.sub_zone_id || null }); setNewItem({ name: "", sub_zone_id: "" }); toast.success("City created"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const save = async (row, patch) => { try { await api.patch(`/geography/cities/${row.id}`, patch); toast.success("Saved"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const del = async (row) => { try { await api.delete(`/geography/cities/${row.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const subZoneOptions = subZones.map((s) => ({ value: s.id, label: s.name }));
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <Label className="text-xs">Zone</Label>
            <Select value={zoneId || "none"} onValueChange={(v) => setZoneId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 w-44 mt-1"><SelectValue placeholder="Pick zone" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— Zone —</SelectItem>{zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">State</Label>
            <Select value={stateId || "none"} onValueChange={(v) => setStateId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 w-56 mt-1" data-testid="geo-cities-state-filter"><SelectValue placeholder="Pick state" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— State —</SelectItem>{states.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        {stateId ? (
          <>
            <SimpleTable
              columns={["Name", "Sub Zone", "Active", ""]}
              rows={items}
              busy={busy}
              canManage={canManage}
              testid="geo-cities-table"
              renderRow={(row) => (
                <EditableGeoRow key={row.id} row={row} fields={[
                  { key: "name" },
                  { key: "sub_zone_id", type: "select", options: subZoneOptions, placeholder: "— Sub Zone —" },
                ]} canManage={canManage} onSave={save} onDelete={del} />
              )}
            />
            {canManage && (
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">New city</Label>
                  <Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="h-9 mt-1" data-testid="geo-city-new-name" />
                </div>
                <div>
                  <Label className="text-xs">Sub Zone <span className="font-normal text-neutral-400">(optional)</span></Label>
                  <Select value={newItem.sub_zone_id || "none"} onValueChange={(v) => setNewItem({ ...newItem, sub_zone_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-9 w-48 mt-1" data-testid="geo-city-new-sub-zone"><SelectValue placeholder="Defaults to state" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— defaults to state —</SelectItem>
                      {subZoneOptions.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={create} disabled={!newItem.name} className="tulsi-gradient text-white h-9" data-testid="geo-city-add">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-neutral-500 py-6 text-center">Select a zone and state.</div>
        )}
      </CardContent>
    </Card>
  );
}

function AreasTab({ canManage }) {
  const [zones, setZones] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState({ name: "" });
  useEffect(() => { api.get("/zones").then((r) => setZones(r.data.items)).catch(() => {}); }, []);
  useEffect(() => {
    if (!zoneId) { setStates([]); setStateId(""); return; }
    api.get("/geography/states", { params: { zone_id: zoneId } }).then((r) => setStates(r.data.items)).catch(() => {});
  }, [zoneId]);
  useEffect(() => {
    if (!stateId) { setCities([]); setCityId(""); return; }
    api.get("/geography/cities", { params: { state_id: stateId } }).then((r) => setCities(r.data.items)).catch(() => {});
  }, [stateId]);
  const load = useCallback(async () => {
    if (!cityId) { setItems([]); return; }
    setBusy(true);
    try { const { data } = await api.get("/geography/areas", { params: { city_id: cityId, include_inactive: true } }); setItems(data.items); }
    catch (e) { toast.error(classifyErr(e)); } finally { setBusy(false); }
  }, [cityId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { try { await api.post("/geography/areas", { name: newItem.name, city_id: cityId, zone_id: zoneId }); setNewItem({ name: "" }); toast.success("Area created"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const save = async (row, patch) => { try { await api.patch(`/geography/areas/${row.id}`, patch); toast.success("Saved"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  const del = async (row) => { try { await api.delete(`/geography/areas/${row.id}`); toast.success("Deactivated"); load(); } catch (e) { toast.error(classifyErr(e)); } };
  return (
    <Card className="border-neutral-200 rounded-2xl">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <Label className="text-xs">Zone</Label>
            <Select value={zoneId || "none"} onValueChange={(v) => setZoneId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 w-44 mt-1"><SelectValue placeholder="Pick zone" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— Zone —</SelectItem>{zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">State</Label>
            <Select value={stateId || "none"} onValueChange={(v) => setStateId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 w-56 mt-1"><SelectValue placeholder="Pick state" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— State —</SelectItem>{states.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">City</Label>
            <Select value={cityId || "none"} onValueChange={(v) => setCityId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 w-56 mt-1" data-testid="geo-areas-city-filter"><SelectValue placeholder="Pick city" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— City —</SelectItem>{cities.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        {cityId ? (
          <>
            <SimpleTable
              columns={["Name", "Active", ""]}
              rows={items}
              busy={busy}
              canManage={canManage}
              testid="geo-areas-table"
              renderRow={(row) => (
                <EditableGeoRow key={row.id} row={row} fields={[{ key: "name" }]} canManage={canManage} onSave={save} onDelete={del} />
              )}
            />
            {canManage && (
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-xs">New area</Label>
                  <Input value={newItem.name} onChange={(e) => setNewItem({ name: e.target.value })} className="h-9 mt-1" data-testid="geo-area-new-name" />
                </div>
                <Button onClick={create} disabled={!newItem.name} className="tulsi-gradient text-white h-9" data-testid="geo-area-add">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-neutral-500 py-6 text-center">Select a city.</div>
        )}
      </CardContent>
    </Card>
  );
}

function SimpleTable({ columns, rows, busy, testid, renderRow }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-200">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-neutral-50">
          <tr>{columns.map((c, i) => (<th key={i} className="px-3 py-2 text-left font-medium text-neutral-600">{c}</th>))}</tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {busy && (<tr><td colSpan={columns.length} className="text-center text-neutral-500 py-6">Loading…</td></tr>)}
          {!busy && rows.length === 0 && (<tr><td colSpan={columns.length} className="text-center text-neutral-500 py-6">No entries.</td></tr>)}
          {!busy && rows.map((r) => renderRow(r))}
        </tbody>
      </table>
    </div>
  );
}

function EditableGeoRow({ row, fields, canManage, onSave, onDelete }) {
  const [state, setState] = useState({ ...row });
  useEffect(() => setState({ ...row }), [row]);
  const dirty = fields.some((f) => state[f.key] !== row[f.key]) || state.is_active !== row.is_active;
  return (
    <tr>
      {fields.map((f) => (
        <td key={f.key} className="px-3 py-2">
          {f.readonly ? (
            <span className="font-mono text-xs">{row[f.key]}</span>
          ) : f.type === "select" ? (
            <Select value={state[f.key] || "none"} onValueChange={(v) => setState({ ...state, [f.key]: v === "none" ? null : v })} disabled={!canManage}>
              <SelectTrigger className="h-9"><SelectValue placeholder={f.placeholder || "—"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— none —</SelectItem>
                {(f.options || []).map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={state[f.key] || ""} onChange={(e) => setState({ ...state, [f.key]: e.target.value })} disabled={!canManage} className="h-9" />
          )}
        </td>
      ))}
      <td className="px-3 py-2">
        <label className="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={state.is_active !== false}
            onChange={(e) => setState({ ...state, is_active: e.target.checked })}
            disabled={!canManage}
          />
          {state.is_active !== false ? "Active" : "Inactive"}
        </label>
      </td>
      <td className="px-3 py-2 text-right space-x-1">
        {canManage && (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={!dirty}
              onClick={() => onSave(row, { ...Object.fromEntries(fields.map((f) => [f.key, state[f.key]])), is_active: state.is_active })}
              className="h-8"
            >
              <Save className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(row)} className="h-8 text-red-500 hover:text-red-600">
              <Trash2 className="h-3 w-3" />
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}
